"use client";

import React from "react";
import { Button } from "@mui/material";
import CloseSharpIcon from "@mui/icons-material/CloseSharp";
import ChevronLeftSharpIcon from "@mui/icons-material/ChevronLeftSharp";
import RemoveRedEyeSharpIcon from "@mui/icons-material/RemoveRedEyeSharp";
import { LazyLoadImage } from "react-lazy-load-image-component";
import confirmOrderLogo from "../../img/order_confirm-logo.png";
import ccLogo from "../../img/parts/Cycle-Circle-Logo-Orange-1.png";
import cashbackCard from "../../img/banners/cashback-card.jpg";
import {
  calculateShippingCost,
  calculateAmountToPay,
  getGarrariBikeQuantity,
  trackSteps,
} from "../../utils/utils";
import { useNavigate } from "react-router";

export default function OrderConfirmation({
  handleClose,
  orderNo,
  cart,
  selectedCoupon,
  useWalletAmount,
  setUseWalletAmount,
  isDoorToDoorChecked,
  freeShipping,
  userDetail,
}) {
  const navigate = useNavigate();
  const garrariBikeQuantity = getGarrariBikeQuantity(cart);
  const doorToDoorShippingCost = garrariBikeQuantity * 550;

  const formatWithCommasWithDecimal = (value) =>
    Number(value || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const currentStep = 3;

  return (
    <div
      className="
        w-[900px] h-[600px] max-h-screen
        flex max-sm:flex-col max-sm:w-full max-sm:h-auto
        items-center justify-between bg-white-1 rounded-md
        p-4 relative z-20
        overflow-hidden max-sm:overflow-y-auto
      "
    >
      {/* Close Button */}
      <CloseSharpIcon
        className="absolute top-3 left-3 text-black cursor-pointer z-50"
        onClick={handleClose}
        style={{ fontSize: "18px" }}
      />

      {/* LEFT SECTION */}
      <div className="w-full sm:w-[60%] h-full flex flex-col items-center sm:pr-4 max-sm:pt-4">
        {/* Header Section */}
        <div className="w-full flex flex-col items-center justify-center py-[30px] sm:px-[100px]">
          <LazyLoadImage
            src={confirmOrderLogo}
            alt=""
            effect="blur"
            className="w-[120px] sm:w-[150px] object-contain"
          />
          <span className="text-[17px] uppercase text-black-1 mt-2">
            Thank You
          </span>
          <span className="text-[14px] font-bold text-black-1/80 text-center">
            Your order is confirmed
          </span>
        </div>

        {/* Order Status */}
        <div className="w-full rounded-md border border-black-1/10 py-3 px-3 sm:px-4 bg-white-1">
          <span className="text-[12px] text-black-1/70">
            Order code <b>#{orderNo}</b> was placed on{" "}
            <b>{new Date().toLocaleDateString("en-IN")}</b> and is currently in
            progress.
          </span>

          {/* Steps */}
          <div className="w-full py-4 flex text-gray-700 bg-white-1 justify-center">
            {trackSteps?.map((step, i) => (
              <div
                key={i}
                className={`step-item relative flex flex-col items-center justify-center w-full ${
                  currentStep === i + 1 && "active"
                } ${i + 1 < currentStep && "complete"}`}
              >
                <div
                  className={`step w-[22px] sm:w-[25px] h-[22px] sm:h-[25px] p-1 flex items-center justify-center z-1 rounded-full font-semibold ${
                    currentStep > i + 1
                      ? "text-white-1 bg-green"
                      : "text-blue bg-gray-200"
                  }`}
                >
                  {step.icon}
                </div>
                <p
                  className={`mt-1 text-[10px] sm:text-[11px] md:text-[12px] text-center ${
                    currentStep > i + 1 ? "text-green" : "text-blue"
                  }`}
                >
                  {step.label}
                </p>
              </div>
            ))}
          </div>

          <span className="text-[12px] text-black-1/70">
            Expected delivery date:{" "}
            <b>
              {new Date(
                Date.now() + 7 * 24 * 60 * 60 * 1000
              ).toLocaleDateString("en-US", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </b>
          </span>
        </div>

        {/* Email Info */}
        <span className="text-[12px] my-3 text-center text-black-1/80">
          We will be sending you an email confirmation to{" "}
          <b>{userDetail?.Email || "your registered email"}</b>
        </span>

        {/* Buttons */}
        <div className="w-full py-[30px] sm:px-5 flex max-sm:flex-col gap-3 items-center justify-between">
          <Button
            fullWidth
            variant="contained"
            onClick={() => navigate("/")}
            sx={{
              height: "40px",
              background: "#283248",
              fontSize: "14px",
              fontWeight: "500",
              borderRadius: "4px",
              color: "white",
              textTransform: "none",
              "&:hover": { background: "#283248" },
            }}
          >
            <ChevronLeftSharpIcon className="mr-2" sx={{ fontSize: "16px" }} />
            Continue Shopping
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={() => navigate("/user/orders")}
            sx={{
              height: "40px",
              background: "#28a447",
              fontSize: "14px",
              fontWeight: "500",
              borderRadius: "4px",
              color: "white",
              textTransform: "none",
              "&:hover": { background: "#28a447" },
            }}
          >
            <RemoveRedEyeSharpIcon className="mr-2" sx={{ fontSize: "16px" }} />
            View My Orders
          </Button>
        </div>
      </div>

      {/* RIGHT SECTION */}
      <div className="w-full sm:w-[40%] h-full border border-black-1/10 rounded-md flex flex-col relative max-sm:mt-5">
        <LazyLoadImage
          src={ccLogo}
          alt="cc-logo"
          className="absolute top-2 left-1/2 -translate-x-1/2 h-[50px]"
          effect="blur"
        />
        <div className="w-full h-[44%] px-3 pt-[80px] relative">
          <LazyLoadImage
            src={cashbackCard}
            alt="cashback-card"
            className="w-full h-full object-fill rounded-md"
            effect="blur"
          />
          <span className="w-full text-white-1 text-[11px] absolute bottom-[25%] left-1/2 -translate-x-1/2 text-center">
            Please click below to check your updated wallet
          </span>
          <Button
            variant="contained"
            sx={{
              height: "30px",
              background: "white",
              fontSize: "12px",
              fontWeight: "500",
              color: "#283248",
              borderRadius: "30px",
              position: "absolute",
              left: "50%",
              bottom: "10%",
              transform: "translateX(-50%)",
              "&:hover": { background: "white" },
            }}
            onClick={() => navigate("/user/wallet")}
          >
            View My Wallet
          </Button>
        </div>

        <div className="w-full h-[56%] flex items-center flex-col justify-end p-4">
          <h3 className="text-[15px] text-blue text-center my-2 font-bold">
            Order Summary
          </h3>
          <div className="w-full flex items-center justify-between py-[6px]">
            <span className="text-[13px] text-blue">Sub Total</span>
            <span className="text-[14px] text-blue">
              ₹
              {formatWithCommasWithDecimal(
                calculateAmountToPay(
                  cart,
                  selectedCoupon?.couponDiscountTaken,
                  useWalletAmount,
                  setUseWalletAmount,
                  isDoorToDoorChecked,
                  freeShipping,
                  selectedCoupon
                )
              )}
            </span>
          </div>
          <div className="w-full flex items-center justify-between py-[6px]">
            <span className="text-[13px] text-blue">Delivery</span>
            <span className="text-[14px] text-blue">
              ₹
              {formatWithCommasWithDecimal(
                freeShipping
                  ? doorToDoorShippingCost
                  : calculateShippingCost(cart) + doorToDoorShippingCost
              )}
            </span>
          </div>
          <div className="w-full flex items-center justify-between py-[6px]">
            <span className="text-[13px] text-blue">Discount</span>
            <span className="text-[14px] text-blue">
              -₹
              {formatWithCommasWithDecimal(
                selectedCoupon?.couponDiscountTaken || 0
              )}
            </span>
          </div>
          <div className="w-full flex items-center justify-between py-[6px]">
            <span className="text-[13px] text-blue">Wallet</span>
            <span className="text-[14px] text-blue">
              -₹{formatWithCommasWithDecimal(useWalletAmount)}
            </span>
          </div>
          <div className="w-full flex items-center justify-between py-[6px]">
            <span className="text-[14px] text-blue font-bold">Total</span>
            <span className="text-[14px] text-blue font-bold">
              ₹
              {formatWithCommasWithDecimal(
                calculateAmountToPay(
                  cart,
                  selectedCoupon?.couponDiscountTaken,
                  useWalletAmount,
                  setUseWalletAmount,
                  isDoorToDoorChecked,
                  freeShipping,
                  selectedCoupon
                )
              )}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
